// The BASE-INDEPENDENT checks — the CI verdicts that cannot depend on the base
// branch. ONE table, ONE matcher. See `../babysit.ts` for the file docblock.
//
// WHY. A red check on a PR is read against the BASE: "is this red on the base too?"
// is what tells a base's own breakage from the diff's (wurk#566,
// `apps/worker/src/babysit/base-refresh.ts`). A check whose verdict CANNOT depend
// on the base breaks that question: the orphan-refs guard
// (`scripts/pr-orphan-refs.ts`) reads the PR's title and body and the branch's
// commit subjects and never the base, so its standing on the base tip says nothing
// about it — and it NEVER justifies an update-branch, whatever its standing there.
//
// A NAME HERE IS A CHECK RUN'S NAME, AND GITHUB EMITS ONE PER JOB — never per step.
// The orphan-refs guard now runs as a JOB OF ITS OWN (`.github/workflows/ci.yml`
// `orphan-refs`, named `Orphaned issue refs (PR only)`), so its verdict arrives under
// that name. Until PR #7489 it was a STEP of the `guards` job, and a PR whose latest
// CI run predates that split still reports it under the JOB's name
// (`Repo-root guards (whole-tree)`) — one run that ALSO carries the job's other
// steps, which read the tree the base's own changes can break, and which on a BASE
// commit runs without the PR-only orphan-refs step at all, so the job's standing there
// is tree guards alone and IS evidence about the base. Naming the job here exempted
// those tree-dependent steps too, and `gradeInherited` then bought PR fix rounds for
// guards failures that were the base's — failed on the base, or already fixed there
// (PR #7489 review). BY NAME, the mixed job's run is an ORDINARY base-dependent check.
//
// THE STEP EVIDENCE IS THE EXCEPTION (`BASE_INDEPENDENT_STEP_OF_JOB`), kept for those
// pre-split runs: the current workflow no longer puts the guard in the mixed job, but a
// PR whose latest run is older still carries one. A red run of the
// mixed job whose ONLY failed step is the orphan-refs one is the guard's verdict
// alone: every tree step passed, so nothing the base carries is red in that run, and
// merging the base in re-runs the same PR-metadata guard over a different tree to the
// same red. The declined-base refusal reads the job's own steps before it buys that
// update-branch (`apps/worker/src/babysit/base-refresh.ts` `declinedRefreshRefused`,
// through the `baseIndependentBySteps` port): ANY other failed step, missing steps, or
// a job that cannot be read leaves the run base-DEPENDENT — fail safe, exactly as a
// name no table holds.
//
// The run-name table (`BASE_INDEPENDENT_CHECKS`) is the classification for the
// guard running as a JOB OF ITS OWN — the workflow's shape since the split: only a run
// that IS the guard can be told apart from the tree guards' by name. Where that job is
// decided is NOT here: the workflow
// (`.github/workflows/ci.yml`) owns the run, and the aggregate gate's own table
// (`scripts/ci-aggregate.ts` `JOB_REQUIREMENTS`) has to classify a job the gate waits
// for, or `Aggregate CI` refuses the run (PR #7489 review). Both matchers fold case,
// spaces, dashes and underscores, so a spelling change does not silently drop a member
// — and a RENAME of the job or the step has to change its table here too.
//
// AN UNKNOWN NAME IS BASE-DEPENDENT (fail safe): a check no table names keeps the
// ordinary base rules, and only a named member — by its own run's name, or by its
// job's step evidence — is exempt from them.

/**
 * The checks whose verdict cannot depend on the base branch.
 *
 * One member: the orphan-refs guard, under the NAME OF ITS OWN CHECK RUN — the
 * workflow job that runs it alone (`.github/workflows/ci.yml` `orphan-refs`, named
 * `Orphaned issue refs (PR only)`). Never the JOB a pre-split run reported it under
 * (`Repo-root guards (whole-tree)`), whose other steps read the tree the base can
 * break (see the header); that job is told apart by its STEPS instead
 * ({@link BASE_INDEPENDENT_STEP_OF_JOB}).
 */
export const BASE_INDEPENDENT_CHECKS = ['Orphaned issue refs (PR only)'] as const;

/**
 * The base-independent STEP a mixed job carries, by the JOB's emitted check name —
 * the run-name table's other half (the header). The current workflow runs the guard
 * as its own job, so this pairing matches only runs from BEFORE that split (PR #7489):
 * a PR whose latest CI run is older still reports the guard as this step of the
 * guards job, and is still recognised here. A red run of the job is
 * base-independent only when its ONLY failed step is this one; who reads the steps
 * and how an unknown is graded is the header's. The step name is the same guard
 * {@link BASE_INDEPENDENT_CHECKS} classifies under its own job's name — this file is
 * the ONE place both doors of it live (`apps/worker` `babysit/orphan-refs-fix.ts`
 * derives its repair pairing from here, never a second copy).
 */
export const BASE_INDEPENDENT_STEP_OF_JOB = {
  'Repo-root guards (whole-tree)': 'Orphaned issue refs (PR only)',
} as const;

/**
 * The one normal form both sides of the match take: lowercased, every run of
 * non-alphanumeric characters collapsed to a single space. `Orphaned issue refs
 * (PR only)`, `orphaned-issue-refs-pr-only` and `Orphaned_Issue_Refs__pr_only` are
 * one name.
 */
function checkNameKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const BASE_INDEPENDENT_KEYS: ReadonlySet<string> = new Set(
  BASE_INDEPENDENT_CHECKS.map((name) => checkNameKey(name)),
);

/** The job-name table, keyed under the same normal form as the run-name matcher. */
const BASE_INDEPENDENT_STEP_KEYS: ReadonlyMap<string, string> = new Map(
  Object.entries(BASE_INDEPENDENT_STEP_OF_JOB).map(([job, step]) => [checkNameKey(job), step]),
);

/**
 * Is this check one whose verdict cannot depend on the base branch? An unknown name
 * is base-DEPENDENT: the ordinary base rules still apply to it.
 */
export function isBaseIndependentCheck(name: string): boolean {
  return BASE_INDEPENDENT_KEYS.has(checkNameKey(name));
}

/**
 * The base-independent step this check's JOB carries, or `null` when it has none.
 * Same folding as the run-name matcher; an unknown name has no step (fail safe), and
 * the guard's OWN run has no pairing — it is classified by its name, not its steps.
 */
export function baseIndependentStepOf(checkName: string): string | null {
  return BASE_INDEPENDENT_STEP_KEYS.get(checkNameKey(checkName)) ?? null;
}

/**
 * The base-independent members of `names`, in the order they were given — the checks
 * a base's own standing says nothing about. BY NAME only: a mixed job's run joins
 * them through its step evidence ({@link baseIndependentStepOf}), not through here.
 */
export function baseIndependentChecksOf(names: readonly string[]): string[] {
  return names.filter((name) => isBaseIndependentCheck(name));
}
